import { Nullable } from 'utils/tsHelpers';
import { MessageMedium } from 'constants/messageMediums';
import { ConfigByMedium } from 'modules/frequencyManagement/lib/types';
import { Campaign } from 'modules/domains/campaign';
import { TimespanEnum } from 'modules/intelligentInsights/types';
import { FREQUENCY_PERIOD } from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/constants';

export enum LiftMetric {
  OpenRateLift = 'openRateLift',
  ClickRateLift = 'clickRateLift',
  UnsubscribeRateLift = 'unsubscribeRateLift',
}

export enum OptimizedChangeMetric {
  OptimizedClickRateChange = 'optimizedClickRateChange',
  OptimizedOpenRateChange = 'optimizedOpenRateChange',
  OptimizedUnsubscribeRateChange = 'optimizedUnsubscribeRateChange',
}

export enum LiftKeyMap {
  Click = 'optimizedClickRate',
  Open = 'optimizedOpenRate',
  Unsubscribe = 'optimizedUnsubscribeRate',
}
export interface PerformanceData {
  largestLiftMedium: FroMessageMediums;
  largestLiftMetric: LiftMetric;
  largestLiftValue: number; // Negative number indicates a 'decrease' in directionality
  largestChangeMedium: FroMessageMediums;
  largestChangeMetric: OptimizedChangeMetric;
  largestChangeValue: number; // Negative number indicates a 'decrease' in directionality
}

export type MessageMediumMetrics = {
  messageMedium: FroMessageMediums;
  holdoutOpens: Nullable<number>;
  optimizedOpens: Nullable<number>;
  holdoutClicks: Nullable<number>;
  optimizedClicks: Nullable<number>;
  holdoutSends: Nullable<number>;
  optimizedSends: Nullable<number>;
  holdoutUnsubscribes: number;
  optimizedUnsubscribes: number;
  holdoutSendSkips: number;
  optimizedSendSkips: number;
  holdoutOpenRate: Nullable<number>;
  optimizedOpenRate: Nullable<number>;
  openRateLift: Nullable<number>;
  holdoutClickRate: Nullable<number>;
  optimizedClickRate: Nullable<number>;
  clickRateLift: Nullable<number>;
  holdoutUnsubscribeRate: number;
  optimizedUnsubscribeRate: number;
  unsubscribeRateLift: number;
  holdoutSendSkipRate: number;
  optimizedSendSkipRate: number;
  holdoutClickRateChange: Nullable<number>;
  optimizedClickRateChange: Nullable<number>;
  holdoutOpenRateChange: Nullable<number>;
  optimizedOpenRateChange: Nullable<number>;
  holdoutUnsubscribeRateChange: number;
  optimizedUnsubscribeRateChange: number;
  holdoutUsers: number;
  optimizedUsers: number;
  weeklyHoldoutSendsPerUser: number;
  weeklyOptimizedSendsPerUser: number;
};

export type MessageTypeMetrics = {
  messageMedium: FroMessageMediums;
  messageTypeId: number;
  holdoutSends: number;
  optimizedSends: number;
  holdoutOpens: Nullable<number>;
  optimizedOpens: Nullable<number>;
  holdoutClicks: Nullable<number>;
  optimizedClicks: Nullable<number>;
  holdoutUnsubscribes: number;
  optimizedUnsubscribes: number;
  holdoutSendSkips: number;
  optimizedSendSkips: number;
  holdoutOpenRate: Nullable<number>;
  optimizedOpenRate: Nullable<number>;
  holdoutClickRate: Nullable<number>;
  optimizedClickRate: Nullable<number>;
  holdoutUnsubscribeRate: number;
  optimizedUnsubscribeRate: number;
  openRateLift: Nullable<number>;
  clickRateLift: Nullable<number>;
  unsubscribeRateLift: number;
  holdoutSendSkipRate: number;
  optimizedSendSkipRate: number;
  topCampaignIds: number[];
};

export interface HistoryItem {
  projectId: number;
  messageMedium: FroMessageMediums;
  startDate: string;
  endDate: string;
  messageTypeId: number;
  optimizedOpens: Nullable<number>;
  holdoutOpens: Nullable<number>;
  optimizedClicks: Nullable<number>;
  holdoutClicks: Nullable<number>;
  optimizedUnsubscribes: number;
  holdoutUnsubscribes: number;
  optimizedOpenRate: Nullable<number>;
  holdoutOpenRate: Nullable<number>;
  optimizedClickRate: Nullable<number>;
  holdoutClickRate: Nullable<number>;
  optimizedUnsubscribeRate: number;
  holdoutUnsubscribeRate: number;
  optimizedSendSkipRate: number;
  holdoutSendSkipRate: number;
  frequencyPeriod: Nullable<number>;
  holdoutUsers: number;
  optimizedUsers: number;
  holdoutSends: number;
  holdoutSendSkips: number;
  optimizedSends: number;
  optimizedSendSkips: number;
  openRateLift: Nullable<number>;
  clickRateLift: Nullable<number>;
  unsubscribeRateLift: number;
}

export type MessageMediumByTimeData = {
  messageMedium: FroMessageMediums;
  startDate: string;
  endDate: string;
  frequencyPeriod: number;
  holdoutSends: Nullable<number>;
  optimizedSends: Nullable<number>;
  holdoutOpens: Nullable<number>;
  optimizedOpens: Nullable<number>;
  holdoutClicks: Nullable<number>;
  optimizedClicks: Nullable<number>;
  holdoutUnsubscribes: Nullable<number>;
  optimizedUnsubscribes: Nullable<number>;
  holdoutSendSkips: Nullable<number>;
  optimizedSendSkips: Nullable<number>;
  holdoutUsers: Nullable<number>;
  optimizedUsers: Nullable<number>;
  holdoutOpenRate: Nullable<number>;
  optimizedOpenRate: Nullable<number>;
  holdoutClickRate: Nullable<number>;
  optimizedClickRate: Nullable<number>;
  holdoutUnsubscribeRate: Nullable<number>;
  optimizedUnsubscribeRate: Nullable<number>;
  openRateLift: Nullable<number>;
  clickRateLift: Nullable<number>;
  unsubscribeRateLift: Nullable<number>;
  holdoutSendSkipRate: Nullable<number>;
  optimizedSendSkipRate: Nullable<number>;
};

export type FrequencyPeriod = (typeof FREQUENCY_PERIOD)[keyof typeof FREQUENCY_PERIOD];

export type MessageMediumByTimeMetric = {
  frequencyPeriod: FrequencyPeriod;
  data: MessageMediumByTimeData[];
};

export interface FroInsightsData {
  cardData: PerformanceData;
  rawData: HistoryItem[];
  messageMediumData: MessageMediumMetrics[];
  messageTypeData: MessageTypeMetrics[];
  messageMediumByTimeData: MessageMediumByTimeMetric[];
}

export interface FrequencyOptimizationInsightsResponse {
  optimizeFrequencyReporting: FroInsightsData;
}

export type MessageTypeCampaignData = {
  campaignId: Campaign['id'];
  campaignName: Campaign['name'];
  templateUrl: string;
  campaignType: string;
};

export type MessageTypeRowData = Pick<
  MessageTypeMetrics,
  'messageTypeId' | 'openRateLift' | 'clickRateLift' | 'unsubscribeRateLift' | 'topCampaignIds'
> & {
  campaigns: Array<MessageTypeCampaignData>;
  messagesSkipped: number;
  name: string;
};

export type TopCampaignData = Pick<
  Campaign,
  'id' | 'name' | 'templateId' | 'campaignTypeDefinition'
>;

export type FroMessageMediums = MessageMedium.Email | MessageMedium.SMS | MessageMedium.Push;

export enum MessageMediumTabs {
  Email = MessageMedium.Email,
  Push = MessageMedium.Push,
  SMS = MessageMedium.SMS,
}
export type MessageMediumTab = keyof typeof MessageMediumTabs;

export enum PerformanceOverTimeLift {
  Open = 'Open',
  Click = 'Click',
  Unsubscribe = 'Unsubscribe',
}

/**
  Context types
 */
export interface FroInsightsCoreContextType {
  fetchCoreData: (nextTimespan: TimespanEnum) => void;
  froInsightsData: FroInsightsData;
  isError: boolean;
  isLoading: boolean;
  isReady: boolean;
  selectedLiftTab: PerformanceOverTimeLift;
  selectedMedium: MessageMediumTab;
  setSelectedLiftTab: (medium: PerformanceOverTimeLift) => void;
  setSelectedMedium: (medium: MessageMediumTabs) => void;
  timeSpan: {
    selectedTimespan: TimespanEnum;
    updateTimespan: (nextTimespan: TimespanEnum) => void;
  };
}

export interface ManagementSettingsContextType {
  frmManagementSettings: ConfigByMedium;
  isError: boolean;
  isLoading: boolean;
  isReady: boolean;
}
